import { useState, createRef } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination
} from '@material-ui/core';
import SearchNotFound from '../SearchNotFound';
import { UserListHead, UserListToolbar } from '../user';
import RemoveRoleButton from './RemoveRoleButton'; // Updated import - single generic component
import { applySortFilter, getComparator } from '../../utils/sort';

const roleTableHead = [{ id: 'role', label: 'Role', alignRight: false }, { id: '' }];

RoleTable.propTypes = {
  roles: PropTypes.array.isRequired,
  updateRoles: PropTypes.func.isRequired,
  // Context props - exactly one should be provided
  team: PropTypes.string,
  user: PropTypes.string,
  // Optional customization
  placeholder: PropTypes.string,
  maxHeight: PropTypes.number,
  defaultRowsPerPage: PropTypes.number
};

export default function RoleTable({
  roles,
  updateRoles,
  team,
  user,
  placeholder = 'Search roles...',
  maxHeight = 350,
  defaultRowsPerPage = 5
}) {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const tableRef = createRef();

  // Validation: ensure exactly one context is provided
  if ((team && user) || (!team && !user)) {
    throw new Error('RoleTable requires exactly one of: team or user prop');
  }

  const isTeamContext = Boolean(team);
  const contextValue = team || user;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (tableRef.current) {
      tableRef.current.scrollIntoView();
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - roles.length) : 0;
  const filteredRoles = applySortFilter(roles, getComparator(order), filterName);
  const isRoleNotFound = filteredRoles.length === 0;

  const renderRemoveButton = (role) => (
    <RemoveRoleButton
      role={role}
      updateTable={updateRoles}
      variant="icononly"
      context={isTeamContext ? 'team' : 'user'}
      target={contextValue}
    />
  );

  return (
    <>
      <UserListToolbar
        disableGutters
        variant="dense"
        sx={{ margin: 0 }}
        placeholder={placeholder}
        filterName={filterName}
        onFilterName={handleFilterByName}
      />
      <TableContainer data-testid="role-table" sx={{ maxHeight }}>
        <Table ref={tableRef} size="small">
          <UserListHead
            order={order}
            orderBy={orderBy}
            headLabel={roleTableHead}
            rowCount={roles.length}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {filteredRoles != null &&
              filteredRoles
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((role, i) => (
                  <TableRow hover key={i} tabIndex={-1}>
                    <TableCell align="left">{role}</TableCell>
                    <TableCell align="right">{renderRemoveButton(role)}</TableCell>
                  </TableRow>
                ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
          {isRoleNotFound && (
            <TableBody>
              <TableRow>
                <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                  <SearchNotFound searchQuery={filterName} />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={roles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
